import React, { useState, useEffect, useCallback } from 'react';
import { SNOOZE_KEY, SNOOZE_DAYS, platformOf as detectPlatform, nextSnooze, snoozeState, readSnooze as readStored } from '../installSnooze';

/**
 * Asking people to put the app on their home screen.
 *
 * It used to ask once, ever. `pwa_dismissed` was a permanent flag, so a single
 * "Not now" retired the prompt for good — and the funnel showed the result:
 * six of seven members had opened the app in an iPhone Safari tab, where Apple
 * will not deliver a notification at all, while only four had installed it.
 *
 * The two platforms are not the same problem, and pressing equally on both
 * would be nagging one of them for nothing:
 *
 *   iOS in a tab  — CANNOT receive push. Installing is the only route to a pick
 *                   reminder, so it's worth being persistent, and worth saying
 *                   plainly what they're missing rather than selling "feels
 *                   native".
 *   Android in a tab — receives push perfectly well in Chrome. Installing is a
 *                   convenience. Ask, accept the answer, and ask again rarely.
 *
 * Nothing here blocks the app. It snoozes, and the snooze gets longer each time,
 * so someone who keeps saying no is eventually left alone.
 */

function isInstalled() {
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
}

export default function InstallPrompt() {
  const [prompt, setPrompt] = useState(null);      // Chromium's beforeinstallprompt
  const [sheet, setSheet] = useState(false);       // the full bottom sheet
  const [bar, setBar] = useState(false);           // the slim persistent reminder
  const [installing, setInstalling] = useState(false);
  const [platform] = useState(() => detectPlatform(window.navigator.userAgent, navigator.maxTouchPoints));

  const snoozeNow = useCallback(() => {
    const s = readStored(localStorage);
    const next = nextSnooze(s.count || 0, platform, Date.now());
    const exhausted = next.exhausted;
    try {
      localStorage.setItem(SNOOZE_KEY, JSON.stringify({ count: next.count, until: next.until }));
    } catch (e) {}
    setSheet(false);
    // On iOS the slim bar stays behind, because the thing they're giving up is
    // every notification the app will ever send. On Android it's a nicety and
    // they've said no, so it goes quiet.
    setBar(platform === 'ios' && !exhausted);
  }, [platform]);

  useEffect(() => {
    if (isInstalled()) return;
    const stored = readStored(localStorage);
    const s = snoozeState(stored, platform, Date.now());
    const snoozed = s.snoozed;

    window.addEventListener('appinstalled', () => {
      try { localStorage.removeItem(SNOOZE_KEY); } catch (e) {}
      setSheet(false); setBar(false);
    });

    if (platform === 'ios') {
      // No beforeinstallprompt on iOS — that event is Chromium-only — so there
      // is no button to offer, only instructions.
      if (snoozed) { setBar(!s.exhausted); return; }
      const t = setTimeout(() => setSheet(true), 12000);
      return () => clearTimeout(t);
    }

    if (snoozed) return;
    const handler = (e) => {
      e.preventDefault();
      setPrompt(e);
      setTimeout(() => setSheet(true), 20000);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [platform]);

  const install = async () => {
    if (!prompt) return;
    setInstalling(true);
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === 'accepted') { setSheet(false); setBar(false); }
    else { setInstalling(false); snoozeNow(); }
    setPrompt(null);
  };

  if (isInstalled()) return null;

  // ── The slim bar: iOS only, sits above the bottom nav, never blocks ────────
  if (bar && !sheet) {
    return (
      <div
        onClick={() => { setBar(false); setSheet(true); }}
        style={{
          position: 'fixed', left: 10, right: 10, bottom: 74, zIndex: 1500,
          background: 'linear-gradient(135deg,#013369,#0a5299)',
          border: '1px solid rgba(127,179,255,0.4)', borderRadius: 10,
          padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 10,
          cursor: 'pointer', boxShadow: '0 6px 20px rgba(0,0,0,0.4)'
        }}
      >
        <span style={{ fontSize: 16 }}>🔔</span>
        <span style={{ flex: 1, fontSize: 12.5, color: '#dde', lineHeight: 1.35 }}>
          Reminders don't work in Safari. Add the app to get them
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#7fb3ff', flexShrink: 0 }}>How</span>
      </div>
    );
  }

  if (!sheet) return null;

  const ios = platform === 'ios';

  return (
    <>
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes urgentPulse {
          0%,100% { box-shadow: 0 0 0 0 rgba(213,10,10,0.4); }
          50%      { box-shadow: 0 0 0 6px rgba(213,10,10,0); }
        }
      `}</style>

      <div
        onClick={snoozeNow}
        style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1998, backdropFilter:'blur(2px)' }}
      />

      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1999,
        background: 'linear-gradient(180deg, #0d1525 0%, #070c18 100%)',
        borderTop: '3px solid #D50A0A',
        borderRadius: '20px 20px 0 0',
        padding: '24px 24px 36px',
        animation: 'slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
        boxShadow: '0 -8px 40px rgba(0,0,0,0.6)',
        maxWidth: 500, margin: '0 auto',
        maxHeight: '86vh', overflowY: 'auto'
      }}>
        <div style={{ width:40, height:4, background:'rgba(255,255,255,0.15)', borderRadius:2, margin:'0 auto 20px' }} />

        <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:16 }}>
          <div style={{ width:52, height:52, borderRadius:14, background:'linear-gradient(135deg,#013369,#D50A0A)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:26, flexShrink:0, boxShadow:'0 4px 16px rgba(213,10,10,0.3)' }}>
            {ios ? '🔔' : '🏈'}
          </div>
          <div>
            <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:20, fontWeight:700, color:'#fff', letterSpacing:0.5 }}>
              {ios ? "DON'T MISS A WEEK" : 'RIVALRY PICKS'}
            </div>
            <div style={{ fontSize:13, color:'#7fb3ff', marginTop:2 }}>
              Add to your home screen
            </div>
          </div>
        </div>

        {/* Lead with what they lose, not with what the app is like. On iOS that
            is a real, specific loss; on Android it genuinely isn't. */}
        <div style={{ color:'#889', fontSize:14, lineHeight:1.6, marginBottom: ios ? 16 : 24 }}>
          {ios
            ? "In Safari, Apple won't let us send you anything. No reminder before picks lock, no result when the week ends. Adding Rivalry Picks to your home screen is the only way to turn those on. Takes about ten seconds."
            : 'Get the full app experience. Opens instantly, no browser bar, feels native. One tap to add it.'}
        </div>

        {ios && (
          <div style={{ background:'rgba(127,179,255,0.08)', border:'1px solid rgba(127,179,255,0.25)', borderRadius:10, padding:'14px 16px', marginBottom:20 }}>
            {[['1', 'Tap the Share button at the bottom of Safari'],
              ['2', 'Scroll down and tap "Add to Home Screen"'],
              ['3', 'Open Rivalry Picks from your home screen']].map(([n, text]) => (
              <div key={n} style={{ display:'flex', gap:10, alignItems:'flex-start', marginBottom: n === '3' ? 0 : 10 }}>
                <span style={{ flexShrink:0, width:20, height:20, borderRadius:'50%', background:'rgba(127,179,255,0.2)', color:'#7fb3ff', fontSize:11, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center' }}>{n}</span>
                <span style={{ fontSize:13, color:'#aab', lineHeight:1.45 }}>{text}</span>
              </div>
            ))}
          </div>
        )}

        <div style={{ display:'flex', gap:12, marginBottom:24 }}>
          {(ios
            ? [['🔔','Reminders'], ['📊','Results'], ['⚡','Instant']]
            : [['⚡','Fast'], ['📲','Native'], ['🏈','Always ready']]
          ).map(([icon, label]) => (
            <div key={label} style={{ flex:1, background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:'10px 8px', textAlign:'center' }}>
              <div style={{ fontSize:20, marginBottom:4 }}>{icon}</div>
              <div style={{ fontSize:11, color:'#778', fontWeight:700 }}>{label}</div>
            </div>
          ))}
        </div>

        {!ios && (
          <button
            onClick={install}
            disabled={installing}
            style={{ width:'100%', padding:'15px', background:'linear-gradient(135deg,#D50A0A,#a00808)', border:'none', borderRadius:10, color:'#fff', fontSize:16, fontWeight:700, cursor:'pointer', fontFamily:"'Oswald',sans-serif", letterSpacing:1, marginBottom:12, opacity: installing ? 0.8 : 1, animation:'urgentPulse 2.5s ease-in-out infinite' }}
          >
            {installing ? 'Opening prompt...' : 'ADD TO HOME SCREEN'}
          </button>
        )}
        <button
          onClick={snoozeNow}
          style={{ width:'100%', padding:'12px', background:'transparent', border:'none', color:'#445', fontSize:14, cursor:'pointer', fontWeight:600 }}
        >
          {ios ? 'Maybe later' : 'Not now'}
        </button>
      </div>
    </>
  );
}
